/*
 * bf-free-gift.js — Brawn & Fox "America" $75 free-tumbler promo.
 * Auto-adds the hidden gift variant when the pre-discount subtotal (excluding the gift) reaches $75,
 * and auto-removes it below. Keyed entirely on the gift variant id.
 *
 * Re-evaluation triggers (robust to this custom cart):
 *   1. DOMContentLoaded — initial state.
 *   2. Dawn pub/sub cartUpdate — referenced as BARE globals `subscribe`/`PUB_SUB_EVENTS`.
 *      (Dawn declares them at top level in pubsub.js/constants.js. `function subscribe` is on window,
 *      but `const PUB_SUB_EVENTS` is a global LEXICAL binding, so `window.PUB_SUB_EVENTS` is undefined —
 *      that was the bug. cart.js references them bare; so do we.)
 *   3. MutationObserver on `#main-cart-items .js-contents` — event-independent fallback so ANY cart
 *      change (remove / qty / add / section re-render) re-evaluates, regardless of what the theme fires.
 *
 * Loop-safety: a single in-flight lock + 150ms debounce; the observer is DISCONNECTED while the script
 * mutates/repaints the cart, then reconnected; our own add/remove use raw fetch (no publish, so they
 * can't retrigger the theme's cart subscribers); reconcile is idempotent (acts only when the cart is
 * actually wrong), so spurious observer fires are cheap no-ops.
 */
(function () {
  'use strict';
  if (window.__bfGiftInitialized) return;
  window.__bfGiftInitialized = true;

  var GIFT_VARIANT_ID = 48905184706779; // America Tumbler (Gift) variant
  var THRESHOLD_CENTS = 7500;           // $75.00
  var DEBOUNCE_MS = 150;

  var running = false;          // in-flight lock (prevents overlapping reconciles)
  var pendingReconcile = false; // a trigger arrived while locked -> re-run after release (no dropped actions)
  var observer = null;
  var debounceTimer = null;

  function onCartPage() {
    return /(^|\/)cart$/.test(window.location.pathname.replace(/\/+$/, ''));
  }
  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(function (r) { return r.json(); });
  }
  // Pre-discount subtotal in cents, EXCLUDING the gift line (so a % code can't drop a qualifying cart < $75).
  function preSubtotalCents(cart) {
    var sum = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (it.variant_id === GIFT_VARIANT_ID) continue;
      sum += (typeof it.original_line_price === 'number') ? it.original_line_price : it.line_price;
    }
    return sum;
  }
  function findGiftLine(cart) {
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].variant_id === GIFT_VARIANT_ID) return cart.items[i];
    }
    return null;
  }
  function addGift() {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: GIFT_VARIANT_ID, quantity: 1, properties: { _gift: 'true' } }] })
    });
  }
  function changeGift(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    });
  }

  // Re-render the cart-items section in place (mirrors cart.js's Section-Rendering refresh) — no full reload.
  function repaintCart() {
    if (!onCartPage()) return Promise.resolve();
    return fetch('/cart?section_id=main-cart-items')
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var src = doc.querySelector('#main-cart-items .js-contents') || doc.querySelector('.js-contents');
        var dst = document.querySelector('#main-cart-items .js-contents');
        // Trusted, same-origin, server-rendered section HTML (Liquid-escaped) — the standard Shopify
        // Section-Rendering pattern, identical to cart.js. This is NOT user input, so injecting it is safe.
        if (src && dst) dst.innerHTML = src.innerHTML;
      })
      .catch(function () { /* swallow — leave the cart as-is on a failed repaint */ });
  }

  // Resolves to true iff the gift state was changed. Operates on an already-fetched cart.
  function reconcile(cart) {
    if (running) { pendingReconcile = true; return Promise.resolve(false); }
    running = true;
    function release() {
      running = false;
      if (pendingReconcile) { pendingReconcile = false; scheduleReconcile(); }
    }
    var sub = preSubtotalCents(cart);
    var gift = findGiftLine(cart);
    var action = null;
    if (sub >= THRESHOLD_CENTS && !gift) {
      action = addGift();                  // qualify + gift absent -> add (422 at the 200 cap is swallowed)
    } else if (sub < THRESHOLD_CENTS && gift) {
      action = changeGift(gift.key, 0);    // no longer qualifying -> remove
    } else if (gift && gift.quantity !== 1) {
      action = changeGift(gift.key, 1);    // defensive: force qty 1 (controls are hidden)
    }
    if (!action) { release(); return Promise.resolve(false); }
    return action.then(function (res) { return res.ok; }).catch(function () { return false; })
      .then(function (changed) { release(); return changed; });
  }

  // Drive the determinate progress fill from the live subtotal — authoritative, so it never depends on the
  // Liquid inline width surviving a re-render. Sets only style.width (an attribute change), which the
  // childList MutationObserver ignores -> cannot loop.
  // Safe to call with the cart fetched BEFORE reconcile: preSubtotalCents excludes the gift line, so the
  // value is identical before/after a gift add/remove; and if the gift was just added the bar (hence the
  // fill) is absent, so this no-ops.
  function setFill(cart) {
    var fill = document.querySelector('#main-cart-items .cart-new__gift-progress-fill');
    if (!fill) return; // no bar in the DOM (gift present, or subtotal >= threshold)
    var pct = preSubtotalCents(cart) / THRESHOLD_CENTS * 100;
    fill.style.width = Math.max(0, Math.min(100, pct)) + '%';
  }

  function reconcileAndRepaint() {
    return getCart().then(function (cart) {
      return reconcile(cart).then(function (changed) {
        if (changed) {
          stopObserving();             // never react to our own repaint writes
          return repaintCart().then(function () { startObserving(); setFill(cart); });
        }
        setFill(cart);                 // gift unchanged, but the subtotal (and bar fill) may have moved
      });
    }).catch(function () {});
  }

  function scheduleReconcile() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () { debounceTimer = null; reconcileAndRepaint(); }, DEBOUNCE_MS);
  }

  function startObserving() {
    if (!window.MutationObserver) return;
    var target = document.querySelector('#main-cart-items .js-contents');
    if (!target) return;
    if (!observer) observer = new MutationObserver(function () { scheduleReconcile(); });
    observer.observe(target, { childList: true, subtree: true });
  }
  function stopObserving() {
    if (observer) observer.disconnect();
  }

  // Subscribe to Dawn's cartUpdate via BARE globals (window.PUB_SUB_EVENTS is undefined: a const has no
  // window property). Retry a few times in case pubsub.js hasn't executed yet (deferred-script ordering).
  function trySubscribe(attemptsLeft) {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === 'bf-free-gift') return; // ignore our own publishes (defensive)
        scheduleReconcile();
      });
    } else if (attemptsLeft > 0) {
      setTimeout(function () { trySubscribe(attemptsLeft - 1); }, 400);
    }
  }

  function init() {
    trySubscribe(3);
    // Event-independent fallback: observe the cart-items container for ANY change.
    if (onCartPage()) startObserving();
    // Initial evaluation through the same safe path.
    reconcileAndRepaint();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
